/**
 * Copyright 2025 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Modified by the CHAP-R1 project from the original Google Appoint-Ready source;
 * see the NOTICE file in the repository root.
 */

import React, { useState, useEffect, useRef } from "react";
import { marked } from "marked";
import parse from "html-react-parser";
import { diffArrays, diffWords } from "diff";
import "./Interview.css";
import DetailsPopup from "../DetailsPopup/DetailsPopup";
import AudioVisualizer from "../AudioVisualizer/AudioVisualizer";

// Global audio element to bypass iOS Safari's strict play() requirements.
// Instantiating at module scope ensures it's unlocked by the very first user interaction anywhere in the app.
let globalAudioElement = null;
if (typeof window !== 'undefined') {
  globalAudioElement = new Audio();
  const unlockAudio = () => {
    // 1. Unlock HTML5 Audio
    if (globalAudioElement) {
      globalAudioElement.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      globalAudioElement.play().catch(() => {});
    }
    
    // 2. Unlock Web Audio API Context
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const audioCtx = new AudioCtx();
      const buffer = audioCtx.createBuffer(1, 1, 22050);
      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(audioCtx.destination);
      source.start(0);
    }

    document.removeEventListener('touchstart', unlockAudio, true);
    document.removeEventListener('click', unlockAudio, true);
  };
  document.addEventListener('touchstart', unlockAudio, { once: true, capture: true });
  document.addEventListener('click', unlockAudio, { once: true, capture: true });
}

const Interview = ({ patientName, onBack, isTrainingMode, isPatientMode, isPrimaryCareMode, isHtnMode, caseData }) => {
  // Client-side safety net: strip LEAKED provenance text only.
  // KEEP citation anchors like <a href="urn:src:turn_1">ⁱ</a> — they power hover tooltips.
  const cleanProvenanceTags = (html) => {
    // 1. Strip bare citation IDs like turn_1, cond_2 that leaked as plain text
    //    (negative lookbehind ensures we don't strip IDs inside href attributes)
    let clean = html.replace(/(?<!["=\/a-zA-Z:])\b(?:turn|cond|med|allergy|rx|hx|social|family|vitals|lab|imaging)_\d+\b(?![")\]a-zA-Z])/g, '');
    // 2. Strip double/triple brackets: [[anything]] or [[[anything]]]
    clean = clean.replace(/\[{2,3}[^\]]*\]{2,3}/g, '');
    // 3. Strip raw markdown citation links that marked() somehow didn't convert
    clean = clean.replace(/\[ⁱ\]\(urn:src:[^)]+\)/g, '');
    // 4. Clean up doubled spaces
    clean = clean.replace(/  +/g, ' ');
    return clean;
  };

  const [messages, setMessages] = useState([]);
  const [isInterviewComplete, setIsInterviewComplete] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluation, setEvaluation] = useState('');
  const [isFetchingEvaluation, setIsFetchingEvaluation] = useState(false);
  const [currentReport, setCurrentReport] = useState("");
  const [rawReport, setRawReport] = useState(""); // Store raw markdown
  const [prevReport, setPrevReport] = useState("");
  // Removed showEvaluationInfoPopup state
  const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);
  const [logFilename, setLogFilename] = useState(null);
  const [caseSummary, setCaseSummary] = useState("");
  const [isFetchingCaseSummary, setIsFetchingCaseSummary] = useState(false);

  // Session management

  // Session management
  const [sessionId, setSessionId] = useState(null);
  const [isWaitingForQuestion, setIsWaitingForQuestion] = useState(false);

  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [inputMode, setInputMode] = useState("voice");
  const [inputText, setInputText] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaStream, setMediaStream] = useState(null); // New state for visualizer

  // Audio playback control
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  // Fix for stale closure in async loops (Training Mode)
  const isAudioEnabledRef = useRef(isAudioEnabled);

  useEffect(() => {
    isAudioEnabledRef.current = isAudioEnabled;
  }, [isAudioEnabled]);

  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const currentPlayingAudio = useRef(globalAudioElement);

  const [showThinkingTraces, setShowThinkingTraces] = useState(!isPatientMode); // New state for thinking traces

  const chatContainerRef = useRef(null);
  // Add state for diagnosis input
  const [diagnosisInput, setDiagnosisInput] = useState('');

  // Training mode auto-response
  const [isGeneratingPatientResponse, setIsGeneratingPatientResponse] = useState(false);
  const latestInterviewerQuestionRef = useRef('');
  const shouldAutoRespondRef = useRef(false);

  // Training mode grading
  const [gradingResults, setGradingResults] = useState(null);
  const [gradingMarkdown, setGradingMarkdown] = useState('');
  const [showGrading, setShowGrading] = useState(false);
  const [isFetchingGrading, setIsFetchingGrading] = useState(false);

  // Interview Confidence scoring
  const [confidenceScore, setConfidenceScore] = useState(null);
  const [showConfidence, setShowConfidence] = useState(false);
  const [isFetchingConfidence, setIsFetchingConfidence] = useState(false);

  // Patient mode submission status: null | 'submitting' | 'complete'
  const [submissionStatus, setSubmissionStatus] = useState(null);

  // Evaluation dropdown state
  const [showEvalDropdown, setShowEvalDropdown] = useState(false);

  // Guerilla Research: Consent state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [userConsented, setUserConsented] = useState(null); // null = not answered, true/false = answered
  const [consentPending, setConsentPending] = useState(true); // Block interview until consent answered

  // Guerilla Research: Survey state
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyData, setSurveyData] = useState({
    simulatedCondition: '',
    historySufficiency: '',
    safetyRedFlags: '',
    noteUtility: '',
    openFeedback: ''
  });
  const [pendingLogFilename, setPendingLogFilename] = useState(null);
  const [emailResults, setEmailResults] = useState(false); // Email checkbox state
  const [recipientEmail, setRecipientEmail] = useState(''); // Blank = server uses NOTIFICATION_EMAIL

  // Citation Sources and Tooltip
  const [citationSources, setCitationSources] = useState({});
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });
  const hoverTimerRef = useRef(null);

  const reportContentRef = useRef(null);
  const lastMessageRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const persistentStreamRef = useRef(null); // Persistent mic stream to avoid permission prompts

  // Grading tooltip state
  const [gradingTooltip, setGradingTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });

  // Patient Mode specific state
  const [clickCount, setClickCount] = useState(0);
  const [showEmailConfigModal, setShowEmailConfigModal] = useState(false);
  const clickTimeoutRef = useRef(null);

  // Load saved patient email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('patientModeTargetEmail');
    if (savedEmail) {
      setRecipientEmail(savedEmail);
      setEmailResults(true);
    }
  }, []);

  const handleHeaderClick = () => {
    if (!isPatientMode) return;
    
    setClickCount(prev => prev + 1);
    
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    clickTimeoutRef.current = setTimeout(() => {
      setClickCount(0);
    }, 1000);
  };

  useEffect(() => {
    if (clickCount >= 3) {
      setShowEmailConfigModal(true);
      setClickCount(0);
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    }
  }, [clickCount]);

  const savePatientEmail = (email) => {
    localStorage.setItem('patientModeTargetEmail', email);
    setRecipientEmail(email);
    setEmailResults(!!email);
    setShowEmailConfigModal(false);
  };

  const baseURL = window.location.origin === "http://localhost:3000"
    ? "http://localhost:7860"
    : "";

  // Initialize interview - but first show consent modal (skip for training mode)
  useEffect(() => {
    if (!patientName) return;

    // Show consent modal on mount - skip for training mode (per user request)
    if (!isTrainingMode) {
      setShowConsentModal(true);
    } else {
      // Training mode: skip consent, proceed immediately
      setConsentPending(false);
    }

    return () => {
      if (currentPlayingAudio.current) {
        currentPlayingAudio.current.pause();
        currentPlayingAudio.current = null;
      }
    };
  }, [patientName]);

  // Start interview after consent is answered
  useEffect(() => {
    if (!patientName || consentPending) return;

    const initInterview = async () => {
      try {
        const response = await fetch(`${baseURL}/api/start_interview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patient_name: patientName,
            condition: "Unknown",
            is_training_mode: isTrainingMode || false,
            is_primarycare_mode: isPrimaryCareMode || false,
            is_htn_mode: isHtnMode || false,
            case_id: caseData?.case_id || null
          })
        });

        const data = await response.json();
        setSessionId(data.session_id);

        // Store consent in session immediately after we have session_id
        if (userConsented !== null) {
          await fetch(`${baseURL}/api/update_consent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: data.session_id,
              consented: userConsented,
              timestamp: new Date().toISOString()
            })
          });
        }

        const initialMessages = [];
        if (data.thinking) {
          initialMessages.push({ speaker: "interviewer thinking", text: `Thoughts: ${data.thinking}` });
        }

        if (data.question && data.question.text) {
          initialMessages.push({
            speaker: "interviewer",
            text: data.question.text,
            audio: data.question.audio
          });
        }

        if (data.citation_sources) {
          setCitationSources(data.citation_sources);
        }

        setMessages(initialMessages);
        playAudio(data.question.audio);

        if (data.interview_complete) {
          setIsInterviewComplete(true);
        } else if (isTrainingMode && data.question && data.question.text) {
          // In training mode, auto-generate patient response after initial question
          // Pass session_id directly since state may not be updated yet
          setTimeout(() => {
            generateAndSubmitPatientResponse(data.question.text, data.session_id);
          }, 2000);
        }
      } catch (error) {
        console.error("Error starting interview:", error);
        setMessages([{ speaker: "system", text: "Error starting interview. Please try again." }]);
      }
    };

    initInterview();
  }, [patientName, baseURL, consentPending, userConsented]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflowY = "clip";
    return () => {
      document.body.style.overflowY = "unset";
    };
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current && lastMessageRef.current) {
      const container = chatContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        container.clientHeight;

      if (isNearBottom && messages.length > 0) {
        lastMessageRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }
  }, [messages, isRecording]);


  // Voice recording functions
  const startRecording = async () => {
    try {
      // Reuse persistent stream if available and still active
      let stream = persistentStreamRef.current;
      if (!stream || stream.getTracks()[0]?.readyState === 'ended') {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          }
        });
        persistentStreamRef.current = stream; // Cache for future recordings
      }

      setMediaStream(stream); // Set stream for visualizer

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        // iOS Safari Fix: We must explicitly stop tracks to avoid flatline / silent recording on subsequent attempts.
        // We scope this to iOS ONLY to avoid the 1-2 second mic spinup delay on desktop.
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if (isIOS && persistentStreamRef.current) {
          persistentStreamRef.current.getTracks().forEach(track => track.stop());
          persistentStreamRef.current = null;
        }
        setMediaStream(null); // Clear visualizer stream
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Could not access microphone. Please check permissions and try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleStopAndSubmit = () => {
    if (mediaRecorderRef.current && isRecording) {
      shouldSubmitAfterStop.current = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const shouldSubmitAfterStop = useRef(false);

  // Pre-initialize microphone when entering voice mode to remove 1-2s delay
  useEffect(() => {
    if (inputMode === "voice" && !persistentStreamRef.current) {
      navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      }).then(stream => {
        persistentStreamRef.current = stream;
      }).catch(err => {
        console.error("Error pre-initializing microphone:", err);
      });
    }
  }, [inputMode]);

  useEffect(() => {
    if (audioBlob && shouldSubmitAfterStop.current) {
      shouldSubmitAfterStop.current = false;
      submitResponse();
    }
  }, [audioBlob]);

  const playAudio = (audioDataUrl) => {
    // Check ref for fresh state (fixes stale closure)
    if (audioDataUrl && isAudioEnabledRef.current) {
      if (currentPlayingAudio.current) {
        currentPlayingAudio.current.pause();
        currentPlayingAudio.current.src = audioDataUrl;
        
        currentPlayingAudio.current.onplay = () => setIsPlayingAudio(true);
        currentPlayingAudio.current.onended = () => setIsPlayingAudio(false);
        currentPlayingAudio.current.onpause = () => setIsPlayingAudio(false);

        currentPlayingAudio.current.play().catch(e => {
          console.error("Error playing audio:", e);
          setIsPlayingAudio(false);
        });
      } else {
        const audio = new Audio(audioDataUrl);
        currentPlayingAudio.current = audio;

        audio.onplay = () => setIsPlayingAudio(true);
        audio.onended = () => setIsPlayingAudio(false);
        audio.onpause = () => setIsPlayingAudio(false);

        audio.play().catch(e => {
          console.error("Error playing audio:", e);
          setIsPlayingAudio(false);
        });
      }
    }
  };

  const handleToggleAudio = () => {
    setIsAudioEnabled(prev => {
      const newValue = !prev;
      // Stop current audio when turning audio OFF (not ON)
      if (!newValue && currentPlayingAudio.current) {
        currentPlayingAudio.current.pause();
        currentPlayingAudio.current.src = '';
        setIsPlayingAudio(false);
      }
      return newValue;
    });
  };

  // Guerilla Research: Handle consent response
  const handleConsentResponse = (consented) => {
    if (!consented) {
      onBack();
      return;
    }
    setUserConsented(consented);
    setShowConsentModal(false);
    setConsentPending(false);
    // Consent will be stored when interview starts (we don't have sessionId yet)
  };

  // Guerilla Research: Handle survey submission
  const handleSurveySubmit = async (skipped) => {
    try {
      // Submit survey data to backend - this now generates the log file
      const response = await fetch(`${baseURL}/api/submit_survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          survey_data: skipped ? null : surveyData,
          skipped: skipped,
          email_results: emailResults,  // Pass email checkbox state
          recipient_email: emailResults ? recipientEmail : null  // Pass custom email if enabled
        })
      });

      const data = await response.json();
      setShowSurveyModal(false);

      // Trigger the actual download using the filename from the response
      const filename = data.log_filename;
      if (filename) {
        window.location.href = `${baseURL}/api/download_log/${filename}`;
      }
      setPendingLogFilename(null);
      setSubmissionStatus('complete');
    } catch (error) {
      console.error("Error submitting survey:", error);
      setShowSurveyModal(false);
      setSubmissionStatus('complete'); // Still mark complete even on error to unblock UI
    }
  };

  // Auto-submit when patient presses Submit to Doctor and all reports are ready
  useEffect(() => {
    if (submissionStatus !== 'submitting') return;
    // Require actual report values to exist (not just fetching flags being false)
    // This avoids a race where confidence scoring hasn't started yet but case synthesis just finished
    const reportsReady = caseSummary && confidenceScore && !isFetchingCaseSummary && !isFetchingConfidence;
    if (reportsReady) {
      handleSurveySubmit(true);
    }
  }, [submissionStatus, isFetchingCaseSummary, isFetchingConfidence, confidenceScore, caseSummary]);

  // Guerilla Research: Handle download button click - show survey first
  const handleDownloadClick = (e, filename) => {
    e.preventDefault();
    // Training mode: skip survey, download directly
    if (isTrainingMode) {
      window.open(`${baseURL}/api/download_log/${filename}`, '_blank');
    } else {
      setPendingLogFilename(filename);
      setShowSurveyModal(true);
    }
  };

  // Grading tooltip handlers for truncated cells
  const handleGradingCellHover = (e) => {
    const cell = e.target.closest('.grading-cell');
    if (!cell) return;
    const fullText = cell.getAttribute('data-full');
    if (fullText && fullText !== cell.textContent) {
      const rect = cell.getBoundingClientRect();
      setGradingTooltip({
        visible: true,
        x: rect.left,
        y: rect.bottom + 5,
        content: fullText
      });
    }
  };

  const handleGradingCellLeave = () => {
    setGradingTooltip({ visible: false, x: 0, y: 0, content: '' });
  };

  // Training mode: Generate patient response using LLM
  const generateAndSubmitPatientResponse = async (question, sessionIdParam = null) => {
    const effectiveSessionId = sessionIdParam || sessionId;
    if (!isTrainingMode || !effectiveSessionId || isGeneratingPatientResponse) return;

    setIsGeneratingPatientResponse(true);

    try {
      // Call backend to generate patient response
      const response = await fetch(`${baseURL}/api/generate_patient_response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: effectiveSessionId,
          question: question
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate patient response');
      }

      const data = await response.json();
      const patientResponse = data.patient_response;

      // Use the patient response in the normal submit flow
      // Set inputText and inputMode, then call submitResponse
      setInputMode("text");
      setInputText(patientResponse);

      // Small delay to ensure state is updated before submit
      setTimeout(() => {
        submitResponseWithText(patientResponse, effectiveSessionId);
      }, 100);

    } catch (error) {
      console.error("Error generating patient response:", error);
      setMessages(prev => [...prev, {
        speaker: "system",
        text: "Error generating patient response. Please try again."
      }]);
      setIsGeneratingPatientResponse(false);
    }
  };

  // Helper to submit with specific text (for training mode)
  const submitResponseWithText = async (responseText, sessionIdParam = null) => {
    const effectiveSessionId = sessionIdParam || sessionId;
    if (isSubmitting || isWaitingForQuestion || !responseText || !effectiveSessionId) return;

    setIsSubmitting(true);
    setIsGeneratingPatientResponse(false);

    // Add patient message to UI
    setMessages(prev => [...prev, { speaker: "patient", text: responseText }]);

    // Clear input
    setInputText("");
    setAudioBlob(null);

    try {
      // Submit response to backend (must complete before get_next_question
      // to avoid race condition on multi-threaded servers like Cloud Run)
      const submitRes = await fetch(`${baseURL}/api/submit_response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: effectiveSessionId,
          response: responseText,
          is_patient_mode: isPatientMode
        })
      });

      const submitData = await submitRes.json();
      setRawReport(submitData.report);
      const newReportHtml = cleanProvenanceTags(marked(submitData.report));
      setCurrentReport(oldReport => {
        setPrevReport(oldReport);
        return newReportHtml;
      });

      if (submitData.citation_sources) {
        setCitationSources(submitData.citation_sources);
      }

      // Get next question (Streaming)
      setIsWaitingForQuestion(true);

      // Initialize a new message for the interviewer
      setMessages(prev => [...prev, { speaker: "interviewer", text: "" }]);

      const response = await fetch(`${baseURL}/api/get_next_question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: effectiveSessionId,
          audio_enabled: isAudioEnabledRef.current // Use ref for fresh state
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentQuestionText = '';
      let interviewJustCompleted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const parts = line.split('\n');
            const eventType = parts[0].replace('event: ', '').trim();
            const dataLine = parts.find(p => p.startsWith('data: '));

            if (dataLine) {
              const rawData = dataLine.replace('data: ', '');
              let data = rawData;
              try {
                if (eventType !== 'audio') {
                  data = JSON.parse(rawData);
                } else if (rawData.startsWith('"') && rawData.endsWith('"')) {
                  data = JSON.parse(rawData);
                }
              } catch (e) { }

              if (eventType === 'text') {
                currentQuestionText += data;
                setIsWaitingForQuestion(false);
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const lastMsg = newMsgs[newMsgs.length - 1];
                  if (lastMsg.speaker === 'interviewer') {
                    lastMsg.text += data;
                  }
                  return newMsgs;
                });
              } else if (eventType === 'thinking') {
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const lastMsg = newMsgs[newMsgs.length - 1];
                  if (lastMsg.speaker === 'interviewer thinking') {
                    lastMsg.text += data;
                    return newMsgs;
                  }
                  if (lastMsg.speaker === 'interviewer') {
                    const secondLastMsg = newMsgs.length > 1 ? newMsgs[newMsgs.length - 2] : null;
                    if (secondLastMsg && secondLastMsg.speaker === 'interviewer thinking') {
                      secondLastMsg.text += data;
                    } else {
                      newMsgs.splice(newMsgs.length - 1, 0, { speaker: "interviewer thinking", text: `Thoughts: ${data}` });
                    }
                    return newMsgs;
                  }
                  newMsgs.push({ speaker: "interviewer thinking", text: `Thoughts: ${data}` });
                  return newMsgs;
                });
              } else if (eventType === 'case_summary') {
                setCaseSummary(data);
              } else if (eventType === 'audio') {
                playAudio(data);
              } else if (eventType === 'complete') {
                // Check if interview is complete (handle both string and boolean)
                if (data === true || data === 'true') {
                  setIsInterviewComplete(true);
                  interviewJustCompleted = true;
                }
              }
            }
          }
        }
      }

      // After stream ends, auto-respond if in training mode and we have a question
      if (isTrainingMode && currentQuestionText && !interviewJustCompleted) {
        console.log("Training mode: triggering patient auto-response for:", currentQuestionText.slice(0, 50));
        const questionToAsk = currentQuestionText;
        setTimeout(() => {
          generateAndSubmitPatientResponse(questionToAsk, effectiveSessionId);
        }, 1500); // Delay to let audio play
      }

    } catch (error) {
      console.error("Error submitting response:", error);
      setMessages(prev => [...prev, {
        speaker: "system",
        text: "Error processing your response. Please try again."
      }]);
    } finally {
      setIsWaitingForQuestion(false);
      setIsSubmitting(false);
    }
  };

  const submitResponse = async () => {
    if (isSubmitting || isWaitingForQuestion) return;

    let responseText = inputText.trim();

    // If using voice mode, transcribe first
    // Note: If called from useEffect, audioBlob is up to date.
    if (inputMode === "voice" && audioBlob) {
      setIsTranscribing(true);
      try {
        const formData = new FormData();
        formData.append('audio', audioBlob);
        formData.append('session_id', sessionId);

        const transcribeRes = await fetch(`${baseURL}/api/transcribe_audio`, {
          method: 'POST',
          body: formData
        });

        if (!transcribeRes.ok) {
          throw new Error('Transcription failed');
        }

        const transcribeData = await transcribeRes.json();
        responseText = transcribeData.transcript;

        if (!responseText) {
          alert("Could not transcribe audio. Please try again or switch to text mode.");
          setIsTranscribing(false);
          return;
        }
      } catch (error) {
        console.error("Transcription error:", error);
        alert("Transcription failed. Please try again or use text mode.");
        setIsTranscribing(false);
        return;
      }
      setIsTranscribing(false);
    }

    if (!responseText) {
      alert("Please provide a response before submitting.");
      return;
    }

    setIsSubmitting(true);

    // Add patient message to UI
    setMessages(prev => [...prev, { speaker: "patient", text: responseText }]);

    // Clear input
    setInputText("");
    setAudioBlob(null);

    try {
      // Submit response to backend (must complete before get_next_question
      // to avoid race condition on multi-threaded servers like Cloud Run)
      const submitRes = await fetch(`${baseURL}/api/submit_response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          response: responseText,
          is_patient_mode: isPatientMode
        })
      });

      const submitData = await submitRes.json();
      setRawReport(submitData.report); // Save raw markdown report
      const newReportHtml = cleanProvenanceTags(marked(submitData.report));
      setCurrentReport(oldReport => {
        setPrevReport(oldReport); // Correctly save the old report
        return newReportHtml;       // Return the new report
      });

      if (submitData.citation_sources) {
        setCitationSources(submitData.citation_sources);
      }

      // Get next question (Streaming)
      setIsWaitingForQuestion(true);

      // Initialize a new message for the interviewer
      setMessages(prev => [...prev, { speaker: "interviewer", text: "" }]);

      const response = await fetch(`${baseURL}/api/get_next_question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          audio_enabled: isAudioEnabledRef.current // Use ref for fresh state
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let interviewJustCompleted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // Keep the last partial line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const parts = line.split('\n');
            const eventType = parts[0].replace('event: ', '').trim();
            const dataLine = parts.find(p => p.startsWith('data: '));

            if (dataLine) {
              const rawData = dataLine.replace('data: ', '');
              let data = rawData;
              try {
                if (eventType !== 'audio') { // Audio is base64, usually safe but json.dumps might add quotes.
                  data = JSON.parse(rawData);
                } else if (rawData.startsWith('"') && rawData.endsWith('"')) {
                  data = JSON.parse(rawData);
                }
              } catch (e) {
                // Keep raw data if parse fails
              }

              if (eventType === 'text') {
                setIsWaitingForQuestion(false); // Stop waiting indicator
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const lastMsg = newMsgs[newMsgs.length - 1];
                  if (lastMsg.speaker === 'interviewer') {
                    lastMsg.text += data;
                  }
                  return newMsgs;
                });
              } else if (eventType === 'thinking') {
                // Handle thinking if we want to show it separately
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const lastMsg = newMsgs[newMsgs.length - 1];

                  // Case 1: The very last message is a thinking bubble
                  if (lastMsg.speaker === 'interviewer thinking') {
                    lastMsg.text += data;
                    return newMsgs;
                  }

                  // Case 2: The last message is the interviewer placeholder
                  if (lastMsg.speaker === 'interviewer') {
                    // Check if the message BEFORE the placeholder is a thinking bubble
                    const secondLastMsg = newMsgs.length > 1 ? newMsgs[newMsgs.length - 2] : null;

                    if (secondLastMsg && secondLastMsg.speaker === 'interviewer thinking') {
                      secondLastMsg.text += data;
                    } else {
                      // No thinking bubble exists yet, insert one before the placeholder
                      newMsgs.splice(newMsgs.length - 1, 0, { speaker: "interviewer thinking", text: `Thoughts: ${data}` });
                    }
                    return newMsgs;
                  }

                  // Fallback
                  newMsgs.push({ speaker: "interviewer thinking", text: `Thoughts: ${data}` });
                  return newMsgs;
                });
              } else if (eventType === 'case_summary') {
                setCaseSummary(data);
              } else if (eventType === 'audio') {
                playAudio(data);
              } else if (eventType === 'complete') {
                if (data === true || data === 'true') {
                  setIsInterviewComplete(true);
                  interviewJustCompleted = true;
                }
              }
            }
          }
        }
      }

    } catch (error) {
      console.error("Error submitting response:", error);
      setMessages(prev => [...prev, {
        speaker: "system",
        text: "Error processing your response. Please try again."
      }]);
    } finally {
      setIsWaitingForQuestion(false);
      setIsSubmitting(false);
    }
  };

  // Diff function for report updates
  const getDiffReport = () => {
    // TREAT CITATION LINKS AS ATOMIC TOKENS to prevent HTML corruption
    // Matches <a href="urn:src:...">...</a> entirely
    const tokenizeHTML = (html) => html.match(/(<a href="urn:src:[^"]+">[^<]+<\/a>|<[^>]+>|[^<]+)/g) || [];
    const tokensPrev = tokenizeHTML(prevReport);
    const tokensCurrent = tokenizeHTML(currentReport);
    const diffParts = diffArrays(tokensPrev, tokensCurrent);

    let result = "";
    for (let i = 0; i < diffParts.length; i++) {
      if (
        diffParts[i].removed &&
        i + 1 < diffParts.length &&
        diffParts[i + 1].added
      ) {
        const removedText = diffParts[i].value.join("");
        const addedText = diffParts[i + 1].value.join("");
        if (!/^<[^>]+>$/.test(removedText) && !/^<[^>]+>$/.test(addedText)) {
          const innerDiff = diffWords(removedText, addedText);
          const innerResult = innerDiff
            .map((part) => {
              if (part.added) {
                return `<span class="add">${part.value}</span>`;
              } else if (part.removed) {
                return `<span class="remove">${part.value}</span>`;
              }
              return part.value;
            })
            .join("");
          result += innerResult;
          i++;
          continue;
        }
      }
      if (diffParts[i].added) {
        result += `<span class="add">${diffParts[i].value.join("")}</span>`;
      } else if (diffParts[i].removed) {
        result += `<span class="remove">${diffParts[i].value.join("")}</span>`;
      } else {
        result += diffParts[i].value.join("");
      }
    }
    return result;
  };

  useEffect(() => {
    if (isInterviewComplete && reportContentRef.current) {
      reportContentRef.current.scrollTop = reportContentRef.current.scrollHeight;
    }
  }, [isInterviewComplete]);

  // Handle Tooltip Hover
  useEffect(() => {
    const reportContainer = reportContentRef.current;
    if (!reportContainer) return;

    const handleMouseOver = (e) => {
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('urn:src:')) {
        const srcId = e.target.getAttribute('href').replace('urn:src:', '');
        const content = citationSources[srcId];

        if (content) {
          // Start 2s timer
          hoverTimerRef.current = setTimeout(() => {
            const rect = e.target.getBoundingClientRect();
            setTooltip({
              visible: true,
              x: rect.left + window.scrollX,
              y: rect.bottom + window.scrollY + 5, // 5px below
              content: content
            });
          }, 2000);
        }
      }
    };

    const handleMouseOut = (e) => {
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('urn:src:')) {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
        setTooltip(prev => ({ ...prev, visible: false }));
      }
    };

    // Prevent default link behavior & Show tooltip on click
    const handleClick = (e) => {
      if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('urn:src:')) {
        e.preventDefault();

        // Show immediately
        const srcId = e.target.getAttribute('href').replace('urn:src:', '');
        const content = citationSources[srcId];
        if (content) {
          // Clear any pending timer
          if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
          }

          const rect = e.target.getBoundingClientRect();
          setTooltip({
            visible: true,
            x: rect.left + window.scrollX,
            y: rect.bottom + window.scrollY + 5,
            content: content
          });
        }
      }
    }

    reportContainer.addEventListener('mouseover', handleMouseOver);
    reportContainer.addEventListener('mouseout', handleMouseOut);
    reportContainer.addEventListener('click', handleClick);

    return () => {
      reportContainer.removeEventListener('mouseover', handleMouseOver);
      reportContainer.removeEventListener('mouseout', handleMouseOut);
      reportContainer.removeEventListener('click', handleClick);
    };
  }, [citationSources, currentReport, prevReport]); // Re-bind when report changes


  // Auto-generate reports when interview completes
  const hasAutoTriggered = useRef(false);
  useEffect(() => {
    if (!isInterviewComplete || hasAutoTriggered.current || !sessionId) return;
    hasAutoTriggered.current = true;

    const runAutomatedTasks = async () => {
      // Auto-trigger case synthesis (both modes)
      setIsFetchingCaseSummary(true);
      const caseSynthesisPromise = fetch(`${baseURL}/api/generate_case_synthesis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(res => res.json())
        .then(data => {
          if (!data.error && data.case_summary) {
            setCaseSummary(data.case_summary);
          }
        })
        .catch(error => console.error('Error auto-generating case synthesis:', error))
        .finally(() => setIsFetchingCaseSummary(false));

      // Patient Mode Final Report Generation
      let patientReportPromise = Promise.resolve();
      if (isPatientMode) {
        patientReportPromise = fetch(`${baseURL}/api/generate_report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        })
          .then(res => res.json())
          .then(data => {
            if (!data.error && data.report) {
              setRawReport(data.report);
              const newReportHtml = cleanProvenanceTags(marked(data.report));
              setCurrentReport(oldReport => {
                setPrevReport(oldReport);
                return newReportHtml;
              });
            }
          })
          .catch(error => console.error('Error auto-generating patient report:', error));
      }

      await Promise.all([caseSynthesisPromise, patientReportPromise]);

      // Auto-trigger confidence scoring (both modes)
      setShowConfidence(true);
      setIsFetchingConfidence(true);
      fetch(`${baseURL}/api/calculate_confidence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            setConfidenceScore({ Quality_Score: 0, Safety_Flag_Color: 'ERROR', Reason: data.error, dimensions: {} });
          } else {
            setConfidenceScore(data.confidence_score);
          }
        })
        .catch(error => {
          console.error('Error auto-calculating confidence:', error);
          setConfidenceScore({ Quality_Score: 0, Safety_Flag_Color: 'ERROR', Reason: error.message, dimensions: {} });
        })
        .finally(() => setIsFetchingConfidence(false));

      // Training mode: also auto-trigger grading (in parallel)
      if (isTrainingMode) {
        setShowGrading(true);
        setIsFetchingGrading(true);
        fetch(`${baseURL}/api/grade_standardized_case`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        })
          .then(res => res.json())
          .then(data => {
            if (data.error) {
              setGradingMarkdown(`**Error:** ${data.error}`);
            } else {
              setGradingResults(data.grading_results);
              setGradingMarkdown(data.grading_markdown);
              if (data.log_filename) {
                setLogFilename(data.log_filename);
              }
            }
          })
          .catch(error => {
            console.error('Error auto-grading case:', error);
            setGradingMarkdown(`**Error:** Failed to grade case. ${error.message}`);
          })
          .finally(() => setIsFetchingGrading(false));
      }
    };

    runAutomatedTasks();
  }, [isInterviewComplete, sessionId, isTrainingMode, baseURL, isPatientMode]);

  // Evaluation logic
  useEffect(() => {
    if (!showEvaluation) return;
    setIsFetchingEvaluation(true);
    setEvaluation('');
    setLogFilename(null);

    fetch(`${baseURL}/api/evaluate_report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        report: rawReport,
        condition: diagnosisInput || "Unknown",
        session_id: sessionId
      })
    })
      .then(response => response.json())
      .then(data => {
        setEvaluation(data.evaluation.replace('```html\n', '').replace('\n```', ''));
        setLogFilename(data.log_filename);
        setIsFetchingEvaluation(false);
      })
      .catch(error => {
        setEvaluation('Error fetching evaluation.');
        setIsFetchingEvaluation(false);
      });
  }, [showEvaluation, currentReport, baseURL, rawReport, diagnosisInput, sessionId]);

  return (
    <div className="page interview-page">
      {/* Guerilla Research: Consent Modal */}
      {showConsentModal && (
        <div className="consent-modal-overlay">
          <div className="consent-modal">
            <h2>Research Consent</h2>
            <div className="consent-text">
              <p><strong>Please review and acknowledge the following:</strong></p>
              <ol>
                <li><strong>Privacy First:</strong> Please DO NOT type your last name, address, phone number, or Alberta Health Number. This system is designed to be anonymous.</li>
                <li><strong>Data Usage:</strong> You consent to Dr. Weldon storing and using this anonymized transcript to improve the CHAP-R1 medical agent and for academic research purposes.</li>
                <li><strong>Care Continuity:</strong> This interview does not replace your visit with the doctor. Your doctor will still perform a full history and physical exam.</li>
                <li><strong>Right to Withdraw:</strong> You can stop the interview at any time.</li>
              </ol>
            </div>
            <div className="consent-buttons">
              <button
                className="consent-button agree"
                onClick={() => handleConsentResponse(true)}
              >
                I Agree
              </button>
              <button
                className="consent-button disagree"
                onClick={() => handleConsentResponse(false)}
              >
                I Disagree
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Mode Email Config Modal */}
      {showEmailConfigModal && (
        <div className="survey-modal-overlay">
          <div className="survey-modal">
            <h2>Patient Mode Configuration</h2>
            <p>Enter the doctor's email address to receive the generated reports.</p>
            <div className="survey-question">
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="doctor@clinic.com"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  marginBottom: '15px'
                }}
              />
            </div>
            <div className="survey-buttons">
              <button className="survey-button skip" onClick={() => setShowEmailConfigModal(false)}>
                Cancel
              </button>
              <button className="survey-button submit" onClick={() => savePatientEmail(recipientEmail)}>
                Save Config
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guerilla Research: Survey Modal */}
      {showSurveyModal && (
        <div className="survey-modal-overlay">
          <div className="survey-modal">
            <h2>CHAP-R1 Physician Feedback</h2>

            <div className="survey-question">
              <label>1. Patient Condition:</label>
              <p className="survey-hint">Enter the diagnosis or chief complaint you were simulating</p>
              <input
                type="text"
                value={surveyData.simulatedCondition}
                onChange={(e) => setSurveyData({ ...surveyData, simulatedCondition: e.target.value })}
                placeholder="e.g., Appendicitis, Migraine"
              />
            </div>

            <div className="survey-question">
              <label>2. History Sufficiency:</label>
              <p className="survey-hint">Did the agent elicit the specific details to suspect the primary diagnosis?</p>
              <select
                value={surveyData.historySufficiency}
                onChange={(e) => setSurveyData({ ...surveyData, historySufficiency: e.target.value })}
              >
                <option value="">-- Select --</option>
                <option value="yes">Yes – It got the full story</option>
                <option value="partially">Partially – It got the main idea but missed key nuances</option>
                <option value="no">No - The history missed major or critical details.</option>
              </select>
            </div>

            <div className="survey-question">
              <label>3. Safety / Red Flags:</label>
              <p className="survey-hint">Did the agent ask critical questions to rule out dangerous differentials?</p>
              <select
                value={surveyData.safetyRedFlags}
                onChange={(e) => setSurveyData({ ...surveyData, safetyRedFlags: e.target.value })}
              >
                <option value="">-- Select --</option>
                <option value="yes">Yes – Thorough safety screen</option>
                <option value="adequate">Adequate – Missed minor questions, but safe</option>
                <option value="unsafe">Unsafe – Missed CRITICAL red flag questions</option>
              </select>
            </div>

            <div className="survey-question">
              <label>4. Note Utility:</label>
              <p className="survey-hint">If this HPI appeared in your chart, what would you do?</p>
              <select
                value={surveyData.noteUtility}
                onChange={(e) => setSurveyData({ ...surveyData, noteUtility: e.target.value })}
              >
                <option value="">-- Select --</option>
                <option value="sign">Sign it (Zero edits)</option>
                <option value="polish">Polish it (&lt;1 min edits)</option>
                <option value="rewrite">Rewrite it (Major edits needed)</option>
                <option value="delete">Delete it (Useless)</option>
              </select>
            </div>

            <div className="survey-question">
              <label>5. Open Feedback:</label>
              <p className="survey-hint">Any specific failures, hallucinations, or "wow" moments?</p>
              <textarea
                value={surveyData.openFeedback}
                onChange={(e) => setSurveyData({ ...surveyData, openFeedback: e.target.value })}
                rows={3}
                placeholder="Optional feedback..."
              />
            </div>

            {/* Email Results Option */}
            <div className="survey-question">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  id="emailResults"
                  checked={emailResults}
                  onChange={(e) => setEmailResults(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="emailResults" style={{ fontWeight: 'normal', cursor: 'pointer', marginBottom: 0 }}>
                  Email results to:
                </label>
              </div>
              {emailResults && (
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="Enter email (blank = default recipient)"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              )}
            </div>

            <div className="survey-buttons">
              <button className="survey-button skip" onClick={() => handleSurveySubmit(true)}>
                Skip Survey
              </button>
              <button className="survey-button submit" onClick={() => handleSurveySubmit(false)}>
                Submit & Download Log
              </button>
            </div>
          </div>
        </div>
      )}

      {!isPatientMode && (
        <div className="headerButtonsContainer">
          <button className="back-button" onClick={onBack}>
            <i className="material-icons back-button-icon">keyboard_arrow_left</i>
            Back
          </button>
        </div>
      )}
      <div className={`frame ${isPatientMode ? 'patient-mode-frame' : ''}`}>
        <div className={`interview-split-container ${isPatientMode ? 'patient-mode' : ''}`}>
          <div className="interview-left-section">
            <div className="interview-chat-panel">
              <div className="header2" onClick={handleHeaderClick} style={{ cursor: isPatientMode ? 'pointer' : 'default' }}>
                Interview with {patientName}
                &nbsp;
                <i
                  className="material-icons toggle-icon"
                  style={{
                    cursor: "pointer",
                    color: isAudioEnabled ? "#1976d2" : "#888",
                  }}
                  title={`Click to ${isAudioEnabled ? "disable" : "enable"} audio`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleAudio();
                  }}
                >
                  {isAudioEnabled ? "volume_up" : "volume_off"}
                </i>
                {/* New thinking trace toggle button - moved inside the header */}
                {!isPatientMode && (
                  <i
                    className="material-icons toggle-icon"
                    style={{
                      cursor: "pointer",
                      color: showThinkingTraces ? "#1976d2" : "#888",
                      marginLeft: "10px", // Add some spacing
                    }}
                    title={`Click to ${showThinkingTraces ? "hide" : "show"} AI thoughts`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowThinkingTraces(prev => !prev);
                    }}
                  >
                    {showThinkingTraces ? "psychology" : "psychology_alt"} {/* Use psychology_alt for hidden state */}
                  </i>
                )}
              </div>

              <div className="chat-container" ref={chatContainerRef}>
                {messages.length === 0 ? (
                  <div className="chat-waiting-indicator">
                    Starting interview...
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      ref={idx === messages.length - 1 ? lastMessageRef : null}
                      className={`chat-message-wrapper ${msg.speaker}${idx === messages.length - 1 ? " fade-in" : ""} ${msg.speaker.includes("interviewer thinking") && !showThinkingTraces ? "hidden-thinking-trace" : ""}`}
                      key={idx}
                    >
                      {msg.speaker.includes("interviewer") && (
                        <img
                          className="chat-avatar"
                          src="assets/ai_headshot.svg"
                          alt="Interviewer"
                        />
                      )}
                      <div className={`chat-bubble ${msg.speaker.includes("thinking") ? "thinking" : ""}`}>
                        {msg.speaker.includes("thinking") ? (
                          <div
                            className="thinking-content"
                            dangerouslySetInnerHTML={{ __html: msg.text }}
                          />
                        ) : (
                          msg.text
                        )}
                      </div>
                      {msg.speaker === "patient" && (
                        <img
                          className="patient-avatar"
                          src={(() => {
                            const name = patientName.trim().toLowerCase();
                            const patients = ['alice', 'mike', 'sukumar'];
                            const matches = patients.filter(p => name.includes(p));

                            if (matches.length === 1) {
                              const matchedName = matches[0].charAt(0).toUpperCase() + matches[0].slice(1);
                              return `/assets/${matchedName}.png`;
                            }
                            return "/assets/generic_patient.png";
                          })()}
                          alt={patientName}
                        />
                      )}
                    </div>
                  ))
                )}

                {isWaitingForQuestion && (
                  <div className="chat-waiting-indicator">
                    Generating next question...
                  </div>
                )}
              </div>

              {/* Audio Visualizer - Now for Microphone */}
              <AudioVisualizer
                mediaStream={mediaStream}
                isActive={isRecording}
              />

              {!isInterviewComplete && (
                <div className="voice-input-container">
                  {isTrainingMode ? (
                    // Training mode: Show status instead of input controls
                    <div className="training-mode-status">
                      {isGeneratingPatientResponse && (
                        <span className="generating-indicator">
                          <i className="material-icons">psychology</i> Generating patient response...
                        </span>
                      )}
                      {isWaitingForQuestion && (
                        <span className="waiting-indicator">
                          <i className="material-icons">psychology</i> Waiting for interviewer...
                        </span>
                      )}
                      {!isGeneratingPatientResponse && !isWaitingForQuestion && !isSubmitting && (
                        <span className="auto-mode-indicator">
                          <i className="material-icons">smart_toy</i> Standardized Patient (Auto-responding)
                        </span>
                      )}
                    </div>
                  ) : (
                    // Normal mode: Voice/Text input controls
                    <>
                      <div className="mode-toggle">
                        <button
                          className={`mode-button ${inputMode === "voice" ? "active" : ""}`}
                          onClick={() => setInputMode("voice")}
                        >
                          <i className="material-icons">mic</i> Voice
                        </button>
                        <button
                          className={`mode-button ${inputMode === "text" ? "active" : ""}`}
                          onClick={() => setInputMode("text")}
                        >
                          <i className="material-icons">keyboard</i> Text
                        </button>
                        {!isInterviewComplete && (
                          <button
                            className="mode-button end-interview"
                            onClick={() => {
                              setIsInterviewComplete(true);
                              setMessages(prev => [...prev, { speaker: "patient", text: "End of Interview." }]);
                            }}
                            disabled={isSubmitting || isWaitingForQuestion}
                            style={{ marginLeft: 'auto', backgroundColor: '#e0e0e0', color: '#333' }}
                          >
                            <i className="material-icons">check_circle</i> {isPatientMode ? 'End Now' : 'End Interview'}
                          </button>
                        )}
                      </div>

                      {inputMode === "voice" ? (
                        <div className="voice-controls">
                          {!isRecording && !audioBlob && (
                            <button
                              className="record-button"
                              onClick={startRecording}
                              disabled={isSubmitting || isWaitingForQuestion}
                            >
                              <i className="material-icons">mic</i>
                            </button>
                          )}

                          {isRecording && (
                            <button
                              className="record-button recording"
                              onClick={handleStopAndSubmit}
                              title="Finish Speaking & Submit"
                            >
                              <i className="material-icons">check</i>
                            </button>
                          )}

                          {audioBlob && !isRecording && (
                            <>
                              <button
                                className="rerecord-button"
                                onClick={() => {
                                  setAudioBlob(null);
                                  startRecording();
                                }}
                              >
                                <i className="material-icons">refresh</i> Re-record
                              </button>
                              <button
                                className="submit-button"
                                onClick={submitResponse}
                                disabled={isSubmitting || isWaitingForQuestion}
                              >
                                {isTranscribing ? "Transcribing..." : "Submit"}
                              </button>
                            </>
                          )}

                          <span className="recording-indicator" style={{ visibility: isRecording ? 'visible' : 'hidden' }}>
                            <i className="material-icons">fiber_manual_record</i> Recording...
                          </span>
                        </div>
                      ) : (
                        <div className="text-controls">
                          <input
                            type="text"
                            className="text-input"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !isSubmitting && !isWaitingForQuestion) {
                                submitResponse();
                              }
                            }}
                            placeholder="Type your response here..."
                            disabled={isSubmitting || isWaitingForQuestion}
                          />
                          <button
                            className="submit-button"
                            onClick={submitResponse}
                            disabled={isSubmitting || isWaitingForQuestion || !inputText.trim()}
                          >
                            Submit
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              
              {/* Submit Flow for Patient Mode */}
              {isPatientMode && isInterviewComplete && (
                <div className="patient-mode-submit-container" style={{ textAlign: 'center', marginTop: '30px', padding: '20px' }}>
                  <h2 style={{ color: '#1976d2', marginBottom: '10px' }}>Thank you, {patientName}.</h2>
                  
                  {submissionStatus === 'complete' ? (
                    // Submission complete
                    <>
                      <p style={{ fontSize: '1.2rem', color: '#2e7d32', fontWeight: 'bold' }}>
                        ✅ Your responses have been submitted to the doctor.
                      </p>
                    </>
                  ) : submissionStatus === 'submitting' ? (
                    // Waiting for reports / submitting
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '10px' }}>
                        <div className="spinner" style={{
                          width: '24px', height: '24px',
                          border: '3px solid #e0e0e0', borderTop: '3px solid #1976d2',
                          borderRadius: '50%', animation: 'spin 1s linear infinite'
                        }} />
                        <p style={{ fontSize: '1.1rem', color: '#555', margin: 0 }}>
                          Preparing your reports&mdash;may take a minute...
                        </p>
                      </div>
                    </>
                  ) : (
                    // Ready to submit
                    <>
                      <p style={{ fontSize: '1.1rem', marginBottom: '20px', color: '#555' }}>Your interview is complete. Please submit your responses to the doctor.</p>
                      <button 
                        className="patient-submit-button"
                        onClick={(e) => {
                          e.preventDefault();
                          setSubmissionStatus('submitting');
                        }}
                        style={{
                          padding: '12px 24px',
                          fontSize: '1.1rem',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      >
                        Submit to Doctor
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="interview-right-section" style={{ display: isPatientMode ? 'none' : 'flex' }}>
            <div className="report-content" ref={reportContentRef}>
              <div className="report-title">Generated Report <span className="provenance-hint">(hover to see provenance)</span></div>
              <div
                dangerouslySetInnerHTML={{
                  __html: prevReport ? getDiffReport() : currentReport,
                }}
              />

              {/* Tooltip */}
              {tooltip.visible && (
                <div
                  className="citation-tooltip"
                  style={{
                    top: tooltip.y,
                    left: tooltip.x
                  }}
                >
                  {tooltip.content.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Case Summary (Clinical Synthesis) */}
            {(caseSummary || isFetchingCaseSummary) && (
              <div className="case-summary-container">
                <div className="header3">Clinical Synthesis</div>
                <div className="case-summary-content">
                  {isFetchingCaseSummary ? (
                    <div>Generating clinical synthesis...</div>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: marked(caseSummary) }} />
                  )}
                </div>
              </div>
            )}

            {showEvaluation && (
              <div className="evaluation-container">
                <div className="header3">Evaluation Report</div>
                <div className="evaluation-content">
                  {isFetchingEvaluation
                    ? <div>Please wait...</div>
                    : (
                      <>
                        {parse(evaluation)}
                      </>
                    )
                  }
                </div>
              </div>
            )}

            {/* Grading results - Training mode only */}
            {showGrading && (
              <div className="grading-container">
                <div className="header3">Graded Standardized Case</div>
                <div
                  className="grading-content"
                  onMouseOver={handleGradingCellHover}
                  onMouseLeave={handleGradingCellLeave}
                >
                  {isFetchingGrading ? (
                    <div>Analyzing interview against rubric...</div>
                  ) : (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: marked(gradingMarkdown) }} />
                    </>
                  )}
                </div>
                {/* Grading tooltip for truncated cells */}
                {gradingTooltip.visible && (
                  <div
                    className="grading-tooltip"
                    style={{
                      left: gradingTooltip.x,
                      top: gradingTooltip.y
                    }}
                  >
                    {gradingTooltip.content}
                  </div>
                )}
              </div>
            )}

            {/* Interview Confidence results */}
            {showConfidence && (
              <div className="confidence-container">
                <div className="header3">Interview Confidence</div>
                <div className="confidence-content">
                  {isFetchingConfidence ? (
                    <div>Calculating confidence score...</div>
                  ) : confidenceScore ? (
                    <>
                      <div className="confidence-header">
                        <span className={`confidence-dot ${confidenceScore.Safety_Flag_Color === 'GREEN' ? 'green' :
                          confidenceScore.Safety_Flag_Color === 'YELLOW' ? 'yellow' :
                            confidenceScore.Safety_Flag_Color === 'RED' ? 'red' : 'error'
                          }`}></span>
                        <span className="confidence-score-value">
                          {confidenceScore.Quality_Score}/100
                        </span>
                        <span className="confidence-flag-label">
                          {confidenceScore.Safety_Flag_Color}
                        </span>
                      </div>
                      <div className="confidence-reason">
                        {confidenceScore.Reason}
                      </div>
                      {confidenceScore.dimensions && Object.keys(confidenceScore.dimensions).length > 0 && (
                        <table className="confidence-dimensions-table">
                          <thead>
                            <tr>
                              <th>Dimension</th>
                              <th>Score</th>
                              <th>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(confidenceScore.dimensions).map(([name, data]) => (
                              <tr key={name}>
                                <td className="dimension-name">{name.replace(/_/g, ' ')}</td>
                                <td className={`dimension-score ${data.score >= 90 ? 'green' : data.score >= 70 ? 'yellow' : 'red'
                                  }`}>{data.score}</td>
                                <td className="dimension-details">{data.details}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {/* Show evaluate/grade dropdown button if interview complete - Moved to bottom */}
            {(isInterviewComplete || messages.some(m => m.text && (m.text.includes("End interview") || m.text.includes("Thank you for answering my questions")))) && (
              <div className="evaluation-input-container">
                {/* Only show diagnosis input if no eval/grading/confidence started */}
                {!showEvaluation && !showGrading && !showConfidence && (
                  <textarea
                    className="diagnosis-input"
                    placeholder="Enter a diagnosis or notes to include in the evaluation..."
                    value={diagnosisInput}
                    onChange={(e) => setDiagnosisInput(e.target.value)}
                  />
                )}
                <div className="eval-dropdown-container">
                  <button
                    className="evaluate-button"
                    onClick={() => {
                      if (!showEvalDropdown) {
                        setShowEvalDropdown(true);
                      } else {
                        // Default action: Evaluate Interview
                        setShowEvalDropdown(false);
                        setShowEvaluation(true);
                      }
                    }}
                  >
                    <i className="material-icons back-button-icon">keyboard_arrow_down</i>
                    Evaluate Interview
                  </button>
                  {showEvalDropdown && (
                    <div className="eval-dropdown-menu">
                      <button
                        className="eval-dropdown-item"
                        onClick={() => {
                          setShowEvalDropdown(false);
                          setShowEvaluation(true);
                        }}
                      >
                        <i className="material-icons">assessment</i>
                        Evaluate Interview
                      </button>
                      {isTrainingMode && (
                        <button
                          className="eval-dropdown-item"
                          onClick={async () => {
                            setShowEvalDropdown(false);
                            setShowGrading(true);
                            setIsFetchingGrading(true);
                            try {
                              const response = await fetch(`${baseURL}/api/grade_standardized_case`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ session_id: sessionId })
                              });
                              const data = await response.json();
                              if (data.error) {
                                setGradingMarkdown(`**Error:** ${data.error}`);
                              } else {
                                setGradingResults(data.grading_results);
                                setGradingMarkdown(data.grading_markdown);
                                if (data.log_filename) {
                                  setLogFilename(data.log_filename);
                                }
                              }
                            } catch (error) {
                              console.error("Error fetching grading:", error);
                              setGradingMarkdown(`**Error:** Failed to grade case. ${error.message}`);
                            } finally {
                              setIsFetchingGrading(false);
                            }
                          }}
                        >
                          <i className="material-icons">grading</i>
                          Grade Standardized Case
                        </button>
                      )}
                      <button
                        className="eval-dropdown-item"
                        onClick={async () => {
                          setShowEvalDropdown(false);
                          setShowConfidence(true);
                          setIsFetchingConfidence(true);
                          try {
                            const response = await fetch(`${baseURL}/api/calculate_confidence`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ session_id: sessionId })
                            });
                            const data = await response.json();
                            if (data.error) {
                              setConfidenceScore({ Quality_Score: 0, Safety_Flag_Color: 'ERROR', Reason: data.error, dimensions: {} });
                            } else {
                              setConfidenceScore(data.confidence_score);
                            }
                          } catch (error) {
                            console.error('Error calculating confidence:', error);
                            setConfidenceScore({ Quality_Score: 0, Safety_Flag_Color: 'ERROR', Reason: error.message, dimensions: {} });
                          } finally {
                            setIsFetchingConfidence(false);
                          }
                        }}
                      >
                        <i className="material-icons">verified</i>
                        Calculate Interview Confidence
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Standalone Download Button - appears when any report is ready */}
            {(showEvaluation || showGrading || showConfidence) && !isFetchingEvaluation && !isFetchingGrading && !isFetchingConfidence && (
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    if (isTrainingMode) {
                      // Training mode: generate log on-demand and download directly
                      try {
                        const res = await fetch(`${baseURL}/api/generate_log`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ session_id: sessionId })
                        });
                        const data = await res.json();
                        if (data.log_filename) {
                          window.open(`${baseURL}/api/download_log/${data.log_filename}`, '_blank');
                        }
                      } catch (err) {
                        console.error('Error generating log:', err);
                      }
                    } else {
                      // Normal mode: show survey first, then generate + download
                      handleDownloadClick(e, 'pending');
                    }
                  }}
                  className="download-button"
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <i className="material-icons" style={{ verticalAlign: 'middle', marginRight: '5px' }}>download</i>
                  {isTrainingMode ? 'Download Interview Log' : 'Survey and Download'}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>



      <DetailsPopup
        isOpen={isDetailsPopupOpen}
        onClose={() => setIsDetailsPopupOpen(false)}
      />
    </div >
  );
};

export default Interview;